// src/services/authService.js - Client-side authentication service
import { getApiUrl } from '../utils/config';

class AuthService {
  constructor() {
    this.tokenKey = 'dixieland_access_token';
    this.refreshTokenKey = 'dixieland_refresh_token';
    this.userKey = 'dixieland_user';
  }

  // Get base URL dynamically
  getBaseUrl() {
    return getApiUrl();
  }

  // Store tokens and user data
  setAuth(accessToken, refreshToken, user) {
    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  // Get stored access token
  getAccessToken() {
    return localStorage.getItem(this.tokenKey);
  }

  // Get stored refresh token
  getRefreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  // Get stored user data
  getUser() {
    const userStr = localStorage.getItem(this.userKey);
    return userStr ? JSON.parse(userStr) : null;
  }

  // Clear all auth data
  clearAuth() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getAccessToken();
  }

  // Make authenticated request
  async authenticatedFetch(url, options = {}) {
    const token = this.getAccessToken();
    
    if (!token) {
      // Fall back to regular fetch with credentials for session-based auth
      return fetch(url, {
        ...options,
        credentials: 'include'
      });
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };

    let response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include' // Still include cookies as fallback
    });

    // If token expired, try to refresh
    if (response.status === 401) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry with new token
        headers.Authorization = `Bearer ${this.getAccessToken()}`;
        response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include'
        });
      }
    }

    return response;
  }

  // Refresh access token
  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      this.clearAuth();
      return false;
    }

    try {
      const response = await fetch(`${this.getBaseUrl()}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.setAuth(data.accessToken, data.refreshToken, data.user);
        return true;
      } else {
        this.clearAuth();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearAuth();
      return false;
    }
  }

  // Exchange session for JWT token (for existing sessions)
  async exchangeSessionForToken() {
    try {
      const response = await fetch(`${this.getBaseUrl()}/auth/token`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.setAuth(data.accessToken, data.refreshToken, data.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session exchange failed:', error);
      return false;
    }
  }

  // Handle OAuth callback
  async handleOAuthCallback() {
    // Check URL parameters for tokens
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const refreshToken = urlParams.get('refreshToken');
    const login = urlParams.get('login');

    if (login === 'success' && token && refreshToken) {
      // Decode token to get user info
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const user = {
          id: payload.id,
          discord_id: payload.discord_id,
          username: payload.username,
          avatar: payload.avatar
        };
        
        this.setAuth(token, refreshToken, user);
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
        return true;
      } catch (error) {
        console.error('Failed to decode token:', error);
      }
    }

    // If no tokens in URL, try to exchange existing session
    const hasSession = await this.exchangeSessionForToken();
    return hasSession;
  }

  // Logout
  async logout() {
    this.clearAuth();
    window.location.href = `${this.getBaseUrl()}/auth/logout`;
  }
}

const authService = new AuthService();
export default authService;